<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <div v-if="visible" class="side-bar-collapsible" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
        <button
            type="button"
            class="side-bar-option side-bar-accordion-header"
            :class="{ expanded: isExpanded, 'has-active-child': hasActiveChild }"
            @click="toggle"
            :title="title"
        >
            <div class="side-bar-option-icon"><i :class="icon"></i></div>
            <div v-if="!isCollapsed" class="side-bar-option-text">{{ title }}</div>
            <div v-if="!isCollapsed" class="side-bar-option-arrow"><i class="fas fa-chevron-down"></i></div>
        </button>

        <transition name="accordion">
            <div v-show="isExpanded && !isCollapsed" class="side-bar-accordion-content">
                <router-link
                    v-for="item in visibleItems"
                    :key="item.name"
                    class="side-bar-option side-bar-sub-option"
                    :class="{ selected: isItemSelected(item) }"
                    :title="item.title"
                    :to="{ name: item.route }"
                >
                    <div class="side-bar-option-text-accordion">{{ item.title }}</div>
                </router-link>
            </div>
        </transition>

        <transition name="accordion-floating-submenu">
            <div
                v-if="isCollapsed && hovering && visibleItems.length > 0"
                class="accordion-floating-submenu"
                :style="{ top: submenuTop + 'px' }"
                @mouseenter="onSubmenuEnter"
                @mouseleave="onSubmenuLeave"
            >
                <div class="accordion-floating-title">{{ title }}</div>
                <router-link
                    v-for="item in visibleItems"
                    :key="item.name"
                    class="accordion-floating-item"
                    :class="{ selected: isItemSelected(item) }"
                    :to="{ name: item.route }"
                    @click="onItemClick"
                >
                    {{ item.title }}
                </router-link>
            </div>
        </transition>
    </div>
</template>

<script lang="ts">
import { PropType, defineComponent } from "vue";
import type { SidebarNavigationItem } from "@/utils/sidebar-navigation";

export type AccordionItemType = SidebarNavigationItem;

export default defineComponent({
    name: "Accordion",
    emits: ["update:expanded"],
    props: {
        title: {
            type: String,
            required: true,
        },
        icon: {
            type: String,
            required: true,
        },
        items: {
            type: Array as PropType<AccordionItemType[]>,
            required: true,
        },
        expanded: {
            type: Boolean,
            default: false,
        },
        currentPage: {
            type: String,
            default: "",
        },
        isCollapsed: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            hovering: false,
            submenuTop: 0,
            hoverTimeout: null as number | null,
            openTimeout: null as number | null,
        };
    },
    computed: {
        isExpanded: {
            get: function (): boolean {
                return this.expanded;
            },
            set: function (value: boolean) {
                this.$emit("update:expanded", value);
            },
        },
        visibleItems: function (): AccordionItemType[] {
            return this.items.filter((item) => item.visible);
        },
        visible: function (): boolean {
            return this.visibleItems.length > 0;
        },
        hasActiveChild: function (): boolean {
            return this.visibleItems.some((item) => this.isItemSelected(item));
        },
    },
    methods: {
        toggle: function () {
            this.isExpanded = !this.isExpanded;
        },
        isItemSelected: function (item: AccordionItemType): boolean {
            if (item.selectedPages && item.selectedPages.length > 0) {
                return item.selectedPages.includes(this.currentPage);
            }
            return this.currentPage === item.route;
        },
        onMouseEnter: function (event: MouseEvent) {
            if (this.hoverTimeout) {
                clearTimeout(this.hoverTimeout);
                this.hoverTimeout = null;
            }

            if (this.openTimeout) {
                clearTimeout(this.openTimeout);
                this.openTimeout = null;
            }

            if (this.isCollapsed && this.visibleItems.length > 0) {
                const target = event.currentTarget as HTMLElement;
                const rect = target.getBoundingClientRect();

                this.openTimeout = window.setTimeout(() => {
                    this.submenuTop = rect.top;
                    this.hovering = true;
                    this.openTimeout = null;
                }, 150);
            }
        },
        onMouseLeave: function () {
            if (this.openTimeout) {
                clearTimeout(this.openTimeout);
                this.openTimeout = null;
            }

            if (this.hoverTimeout) {
                clearTimeout(this.hoverTimeout);
            }

            this.hoverTimeout = window.setTimeout(() => {
                this.hovering = false;
            }, 250);
        },
        onSubmenuEnter: function () {
            if (this.hoverTimeout) {
                clearTimeout(this.hoverTimeout);
                this.hoverTimeout = null;
            }
        },
        onSubmenuLeave: function () {
            if (this.hoverTimeout) {
                clearTimeout(this.hoverTimeout);
            }

            this.hovering = false;
        },
        onItemClick: function () {
            this.hovering = false;
            if (this.hoverTimeout) {
                clearTimeout(this.hoverTimeout);
            }
            if (this.openTimeout) {
                clearTimeout(this.openTimeout);
            }
        },
    },
    beforeUnmount: function () {
        if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
        }
        if (this.openTimeout) {
            clearTimeout(this.openTimeout);
        }
    },
});
</script>

<style scoped>
.accordion-floating-submenu {
    position: fixed;
    left: var(--side-bar-compact-size);
    background: var(--black-color);
    padding: 8px 0;
    border-radius: 8px;
    min-width: 220px;
    z-index: 9999;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.15);
    margin-left: 8px;
}

.accordion-floating-title {
    color: var(--white-color);
    font-weight: 600;
    font-size: 14px;
    padding: 8px 16px;
    margin-bottom: 4px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.05);
}

.accordion-floating-item {
    display: block;
    padding: 10px 16px;
    color: var(--white-color);
    text-decoration: none;
    font-size: 14px;
    transition: background-color 0.2s ease;
}

.accordion-floating-item:hover {
    background: var(--primary-color-dark);
}

.accordion-floating-item.selected {
    background: var(--primary-color);
    font-weight: 500;
}

/* Transition for the floating submenu */
.accordion-floating-submenu-enter-active,
.accordion-floating-submenu-leave-active {
    transition: all 0.2s ease;
}

.accordion-floating-submenu-enter-from {
    opacity: 0;
    transform: translateX(-10px);
}

.accordion-floating-submenu-leave-to {
    opacity: 0;
    transform: translateX(-10px);
}

/* Transition for the accordion */
.accordion-enter-active,
.accordion-leave-active {
    transition: all 0.3s ease;
    max-height: 500px;
}

.accordion-enter-from,
.accordion-leave-to {
    opacity: 0;
    max-height: 0;
}

.side-bar-accordion-header.has-active-child {
    position: relative;
}

.side-bar-accordion-header.has-active-child::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 70%;
    background: var(--prussian-blue-500);
}
</style>
